import { ITCIApplicationRequest, ITCIApplicationResponse, ITCITenantServiceProvider } from "./tci-tenant";
import { strEnum } from "../../types";
import { Observable } from "rxjs";
import { ITCIValidationResult } from "./tci-validation";
import { ITCIFileMap } from "./tci-util";
import { ITCIApplicationDeployResponse } from "./tci-app-deploy";
import { ITCIApplication } from "./tci-application";

/**
 * TCI_UPDATE_ACTION_TYPE
 * @type {{[K in string]: K}}
 */
export const TCI_UPDATE_ACTION_TYPE = strEnum([
    "APP_NAME",
    "APP_DESCRIPTION",
    "APP_ENDPOINT",
    "APP_ENDPOINT_VISIBILITY",
    "UPLOAD_EXISTING",
    "UPDATE_TAGS"
]);
export type TCI_UPDATE_ACTION_TYPE = keyof typeof TCI_UPDATE_ACTION_TYPE;

/**
 * interface ITCIApplicationUpdateRequest
 */
export interface ITCIApplicationUpdateRequest extends ITCIApplicationRequest {
    /**
     * Update Action Type
     */
    actionType: TCI_UPDATE_ACTION_TYPE;
    /**
     * application name to be used in naming actions input
     */
    newApplicationName?: string;
    /**
     * application description to be set for the update
     */
    newApplicationDescription?: string;
    /**
     * Endpoint visibility status used in tenancy action input
     */
    endpointVisibility?: string;
    /**
     * Uploaded file artifacts
     */
    appData?: ITCIFileMap | Uint8Array | any;
}
export interface ITCIApplicationEndpointUpdateRequest {
    title?: string;
    description?: string;
}

/**
 * interface ITCIApplicationUpdateResponse
 */
export interface ITCIApplicationUpdateResponse extends ITCIApplicationResponse {
    /**
     * Update Action Type
     */
    actionType: TCI_UPDATE_ACTION_TYPE;
}

/**
 * interface @ITCIApplicationUpdateProvider
 */
export interface ITCIApplicationUpdateProvider extends ITCITenantServiceProvider {
    /**
     *
     * @param input
     */
    canUpdateApplication(input: ITCIApplicationUpdateRequest): Observable<boolean>;

    /**
     *
     * @param input
     */
    updateApplication(input: ITCIApplicationUpdateRequest): Observable<ITCIApplicationUpdateResponse>;


    /**
     * Validate the files selected by the user
     * The validation result can be an error or warning
     * @param input
     */
    validate(input: ITCIApplicationUpdateRequest): Observable<ITCIValidationResult>;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    startUpdateSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    stopUpdateSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Disconnected Components that take actions related to a application name desc update
     * which impacts the Component state need to enclose the
     * actions within the callback so the update provider can let
     * other those dependencies get notified on the completion of the action
     * for performing refresh or any other action
     * @param input
     * @param callback
     */
    pushAppChanges(input: ITCIApplicationUpdateRequest,
        callback: (cinput: ITCIApplicationUpdateRequest)
            => Observable<ITCIApplicationUpdateResponse>): Observable<ITCIApplicationUpdateResponse>;

    /**
     * Return state if the provider is busy waiting for the pushAppChanges to finish
     * @param input
     */
    isBusy(input: ITCIApplicationUpdateRequest): Observable<boolean>;

    /**
     * Set the editing field state
     * @param isDirty True: Is dirty, False: Is pristine
     */
    setDirty(isDirty: boolean, type: TCI_UPDATE_ACTION_TYPE): void;
}
